import { useCallback, useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { PetitionAngleData } from '#src/app/shared/api/socialStarter';
import { useCookiePref } from '#src/app/shared/hooks/session';

import { generateImagePreparation, type ImagePreparation, reportPrewarmPersistenceError } from './imagePreparation';
import { preparePersistedPrewarmedImage } from './prewarmImage';
import { saveClaimedDraft } from './saveClaimedDraft';

async function startImagePrewarm(
	prepareImageOnce: (draft: PetitionAngleData) => Promise<ImagePreparation>,
	prewarmImageDraft: PetitionAngleData,
	utilityContext: UtilityContext,
): Promise<void> {
	try {
		await prepareImageOnce(prewarmImageDraft);
	} catch (error) {
		reportPrewarmPersistenceError(utilityContext, error);
	}
}

export function useSaveClaimedDraft(
	prewarmImageDraft: PetitionAngleData | undefined,
	socialStarterId: string,
	utilityContext: UtilityContext,
): (draft: PetitionAngleData) => Promise<void> {
	const queryClient = useQueryClient();
	const canPersistPrewarm = useCookiePref('preferences');
	const preparationCacheRef = useRef<{ key: string; promise: Promise<ImagePreparation> } | null>(null);
	const prepareImageOnce = useCallback(
		async (draft: PetitionAngleData) => {
			const imageDraft = prewarmImageDraft ?? draft;
			const cacheKey = `${socialStarterId}:${imageDraft.id}:${String(canPersistPrewarm)}`;
			if (preparationCacheRef.current?.key !== cacheKey) {
				// A rejected preparation must not stay cached, or the CTA would keep failing until reload.
				const promise = (async (): Promise<ImagePreparation> => {
					try {
						return await (prewarmImageDraft && canPersistPrewarm
							? preparePersistedPrewarmedImage(imageDraft, socialStarterId, queryClient, utilityContext)
							: generateImagePreparation(imageDraft, utilityContext));
					} catch (error) {
						if (preparationCacheRef.current?.key === cacheKey) preparationCacheRef.current = null;
						throw error;
					}
				})();
				preparationCacheRef.current = { key: cacheKey, promise };
			}
			return preparationCacheRef.current.promise;
		},
		[canPersistPrewarm, prewarmImageDraft, queryClient, socialStarterId, utilityContext],
	);

	useEffect(() => {
		if (!prewarmImageDraft) return;
		// Starting on mount is the core UX tradeoff: unclaimed jobs are possible, but all page dwell
		// becomes useful generation time instead of making the CTA begin the full wait. Without
		// preferences consent, the in-memory promise provides the head start without draft storage.
		void startImagePrewarm(prepareImageOnce, prewarmImageDraft, utilityContext);
	}, [prepareImageOnce, prewarmImageDraft, utilityContext]);

	return useCallback(
		async (draft: PetitionAngleData) => {
			const preparation = await prepareImageOnce(draft);
			await saveClaimedDraft(preparation, draft, socialStarterId, queryClient);
		},
		[prepareImageOnce, queryClient, socialStarterId],
	);
}
